import { Link } from "@material-ui/core";
import es from "date-fns/locale/es";
import fr from "date-fns/locale/fr";
import { useState } from "react";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Select from "react-select";
import { translate } from "react-switch-lang";
import { Button, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import signUpImage from "../../../assets/images/signup/signup.jpg";
import { establishment, nationalities, occupations, sexes } from "../../../constant";
import { Registration } from "../../../models/registrationDto";
import { useHttpClient } from "../../../security/hooks/axiosProvider";
import { RootState } from "../../../store";
import styles from "./register.module.scss";

const RegisterPage: React.FC<{ t: (key: string) => {} }> = props => {
  registerLocale("es", es);
  registerLocale("fr", fr);

  const initialDate = new Date("01/01/1990");

  const [togglePassword, setTogglePassword] = useState(false);
  const history = useHistory();
  const httpClient = useHttpClient(false);
  const trainingPathTranslationId = new URLSearchParams(useLocation().search).get("requested-training-path");
  const currentLang = useSelector((state: RootState) => state.language.currentLang);

  setDefaultLocale(currentLang);
  const labelKey = `${currentLang}_label`;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    getValues,
  } = useForm<Registration>({ mode: "onChange" });

  const HideShowPassword = () => {
    setTogglePassword(!togglePassword);
  };
  const otherOccupationValidator = () => getValues("occupation") === "Others";

  const otherEstablishmentValidator = () => getValues("establishment") === "Other";
  
  const [modal, setModal] = useState(false);
  const [userName, setUserName] = useState(null); // Nouvel état pour stocker l'ID utilisateur

  const toggle = () => setModal(!modal);

  const checkEmailExists = (email) => {
    return new Promise((resolve, reject) => {
      httpClient.current?.get(`/signup`, { params: { email: email } })
        .then(response => {
          const emailExists = response.data;
          resolve(emailExists);
        })
        .catch(error => {
          reject(error); 
        });
    });
  };

  const saveRegistration = (data: Registration) => {
    if (otherOccupationValidator()) {
      data.occupation = getValues("otherOccupation");
    }
    if (otherEstablishmentValidator()) {
      data.establishment = getValues("otherEstablishment");
    }
    if (isFormValid()) {
      checkEmailExists(data.email)
        .then(emailExists => {
          if (!emailExists) {
            httpClient.current?.post(`/signup`, data, { 
                params: trainingPathTranslationId ? { trainingPathTranslationId: trainingPathTranslationId } : {} 
            })
            .then((response) => {
                const userName = response.data;
                setUserName(userName);      
                toggle();
            })
            .catch((error) => {
                console.error("Erreur lors de la création de l'utilisateur : ", error);
            });

          } 
        })
    }
  };

  const validateEmail = async (value: string) => {
    const emailExists = await checkEmailExists(value); 
    return emailExists ? false : true;  
  };


  const redirectToLogin = () => {
    history.push("/training-paths");
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "green" : "white",
    }),
  };

  const passwordIdenticalValidator = (confirmedPassword: string) => confirmedPassword === getValues("password");

  const validatePassword = (value) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{12,}$/;
    return regex.test(value);
  };

  const isFormValid = () => {
    return isValid;
  };

  return (
    <Container fluid={true} className="p-0">
      <Row>
        <Col xl="7" className={styles["cover-image__container"]}>
          <img className={styles["cover-image"]} src={signUpImage} alt="looginpage" />
        </Col>
        <Col xl="5" xs="12" className="p-0">
          <div className={styles["login-card"]}>
            <div className={styles["login-main-card"]}>
              <Row>
                <Col xl="12" md="12" xs="12">
                  <div className={styles["login-main"]}>
                    <Form className={styles["theme-form"]} onSubmit={handleSubmit(saveRegistration)}>
                      <h4>{props.t("signup_page_title")}</h4>
                      <FormGroup>
                        <Label className="col-form-label pt-0">
                          {props.t("signup_your_name")}
                          <span style={{ color: "red" }}>*</span>
                        </Label>
                        <div className="form-row">
                          <Col xs="6">
                            <Input className="form-control" type="text" required placeholder={`${props.t("signup_last_name")}`} {...register("lastName", { required: true })} />
                            {errors.lastName && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                          </Col>
                          <Col xs="6">
                            <Input className="form-control" type="text" required placeholder={`${props.t("signup_first_name")}`} {...register("firstName", { required: true })} />
                            {errors.firstName && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                          </Col>
                        </div>
                      </FormGroup>

                      <FormGroup>
                        <Label className="col-form-label">
                          {props.t("signup_email_address")}
                          <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                          className="form-control"
                          type="email"
                          required
                          placeholder="test@gmail.com"
                          {...register("email", {required: true,validate: validateEmail, })}
                        />
                       {errors.email && errors.email.type === "required" && (
                       <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>
                     )}
                      {errors.email && errors.email.type === "validate" && (
                      <p className={styles["error_message"]}>{props.t("signup_field_email_not_exists")}</p>
                     )}
                      </FormGroup>

                      <FormGroup>
                        <Label className="col-form-label">
                          {props.t("signup_birth_date")}
                          <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="birthDate"
                          rules={{ required: true }}
                          render={({ field: { onChange } }) => {
                          const handleDateChange = (date: Date | null) => {
                            if (date) {
                              onChange(date.toISOString().slice(0, 10)); 
                            }
                          };

                      return (
                       <DatePicker
                         openToDate={initialDate} 
                         className="form-control"
                         selected={getValues("birthDate") ? new Date(getValues("birthDate") as string) : null} 
                         dateFormat="yyyy" 
                         showYearPicker 
                         onChange={handleDateChange} 
                      />
                    );
                  }}
                 />
                        {errors.birthDate && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                      </FormGroup>

                      <FormGroup>
                        <div className="form-row">
                          <Col xs="6">
                            <Label className="col-form-label">
                              {props.t("signup_nationality")}
                              <span style={{ color: "red" }}>*</span>
                            </Label>
                            <Controller
                              control={control}
                              name="nationality"
                              rules={{ required: true }}
                              render={({ field: { onChange } }) => (
                                <Select
                                  styles={customStyles}
                                  options={currentLang === "fr" ? nationalities : nationalities.sort((a, b) => (a.en_label < b.en_label ? -1 : 1))}
                                  getOptionLabel={option => option[labelKey]}
                                  onChange={e => onChange(e.value)}
                                />
                              )}
                            />
                            {errors.nationality && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                          </Col>
                          <Col xs="6">
                            <Label className="col-form-label">
                              {props.t("signup_genre")}
                              <span style={{ color: "red" }}>*</span>
                            </Label>
                            <Controller
                              control={control}
                              name="sex"
                              rules={{ required: true }}
                              render={({ field: { onChange } }) => <Select styles={customStyles} options={sexes} getOptionLabel={option => option[labelKey]} onChange={e => onChange(e.value)} />}
                            />
                            {errors.sex && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                          </Col>
                        </div>
                      </FormGroup>

                      <FormGroup>
                        <Label className="col-form-label">
                          {props.t("signup_occupation")}
                          <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="occupation"
                          rules={{ required: true }}
                          render={({ field: { onChange } }) => <Select styles={customStyles} options={occupations} getOptionLabel={option => option[labelKey]} onChange={e => onChange(e.value)} />}
                        />
                        {errors.occupation && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                      </FormGroup>

                      {otherOccupationValidator() && (
                        <FormGroup>
                          <Label className="col-form-label pt-0">
                            {props.t("signup_other_occupation")}
                            <span style={{ color: "red" }}>*</span>
                          </Label>
                          <div className="form-row">
                            <Col>
                              <Input
                                className="form-control"
                                type="text"
                                required
                                placeholder={props.t("signup_other_occupation") + ""}
                                {...register("otherOccupation", { required: otherOccupationValidator() })}
                              />
                              {errors.otherOccupation && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                            </Col>
                          </div>
                        </FormGroup>
                      )}

                      <FormGroup>
                        <Label className="col-form-label">
                          {props.t("establishment")}
                          <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="establishment"
                          rules={{ required: true }}
                          render={({ field: { onChange } }) => <Select styles={customStyles} options={establishment} getOptionLabel={option => option[labelKey]} onChange={e => onChange(e.value)} />}
                        />
                        {errors.establishment && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                      </FormGroup>

                      {otherEstablishmentValidator() && (
                        <FormGroup>
                          <Label className="col-form-label pt-0">
                            {props.t("signup_other_establishment")}
                            <span style={{ color: "red" }}>*</span>
                          </Label>
                          <div className="form-row">
                            <Col>
                              <Input
                                className="form-control"
                                type="text"
                                required
                                placeholder={props.t("signup_other_establishment") + ""}
                                {...register("otherEstablishment", { required: otherEstablishmentValidator() })}
                              />
                              {errors.otherEstablishment && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                            </Col>
                          </div>
                        </FormGroup>
                      )}

                      <FormGroup className={styles["password-wrapper"]}>
                        <Label className="col-form-label">
                          {props.t("signup_password")}
                          <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input className="form-control" type={togglePassword ? "text" : "password"} name="password" required {...register("password", { required: true, validate: validatePassword })} />
                        {errors.password && errors.password.type === "required" && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                        {errors.password && errors.password.type === "validate" && <p className={styles["error_message"]}>{props.t("signup_field_password_not_valid")}</p>}
                        <div className={styles["show-hide"]} onClick={() => HideShowPassword()}>
                          <span>{togglePassword ? <i className="fa fa-eye-slash"></i> : <i className="fa fa-eye"></i>}</span>
                        </div>
                      </FormGroup>

                      <FormGroup>
                        <Label className="col-form-label">
                          {props.t("signup_password_confirm")}
                          <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input className="form-control" type="password" name="onfirm-password" required {...register("confirmPassword", { required: true, validate: passwordIdenticalValidator })} />
                        {errors.confirmPassword && errors.confirmPassword.type === "required" && <p className={styles["error_message"]}>{props.t("signup_field_required")}</p>}
                        {errors.confirmPassword && errors.confirmPassword.type === "validate" && <p className={styles["error_message"]}>{props.t("signup_field_password_not_identical")}</p>}
                      </FormGroup>

                      <FormGroup className="mb-0">
                        <p>{`${props.t("signup_form_descreption_rgpd")}`}</p>
                        <p>{`${props.t("signup_form_rgpd")}`}</p>
                        <Link href={currentLang === 'fr' ? "https://www.agrisud.org/web/donnees-personnelles" : 
                                    "https://www.agrisud.org/web/en/personal-data"} target="_blank" rel="noreferrer">
                          <p>{`${props.t("signup_form_rgpd_more")}`}</p>
                        </Link>
                      </FormGroup>

                      <div className={styles["register-actions"]}>
                        <Button className={styles["register-btn"]} disabled={!isFormValid()} color="primary" outline={false}>{`${props.t("signup_validate")}`}</Button>
                        <Button className={styles["register-btn"]} onClick={() => history.goBack()} color="secondary" outline>{`${props.t("signup_cancel")}`}</Button>
                      </div>
                    </Form>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>
      <Modal className={styles["modal-aide"]} isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>{props.t("message_confirmation_title")}</ModalHeader>
        <ModalBody>
          <>
            {props.t("message_confirmation_text")}
            <br />
            {props.t("message_confirmation_user_name")}
            {userName}
          </>
          
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={redirectToLogin}>
            OK
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default translate(RegisterPage);
